// composables/usePedidoForm.js
// Lógica completa del wizard + persistencia de borrador.

import { ref, computed, watch } from "vue"
import Swal from "sweetalert2"
import { crearPedido, analizarPdfPedido, agregarProyectoPedido } from "@/utils/api_backend"
import { DIVISAN_COLORS } from "@/config/palette"
import {
  guardarDraftLS, cargarDraftLS, limpiarDraftLS, hayDraftLS,
  guardarPdfIDB, cargarPdfIDB, limpiarPdfIDB, limpiarTodoDraft,
} from "./usePedidoDraft.js"

export function usePedidoForm(catArticulos) {

  // ── Wizard ───────────────────────────────────────────────
  const paso  = ref(1)
  const pasos = ["Datos del pedido", "Artículos", "Proyectos"]
  const guardando  = ref(false)
  const analizando = ref(false)

  // ── PDF Microsip ─────────────────────────────────────────
  const pdfFile      = ref(null)
  const itemsDesdeIa = ref(false)

  // ── Proyectos de diseño (Paso 3) ─────────────────────────
  // Cada entrada: { archivo: File, _preview: bool, _objectUrl: string }
  const proyectos = ref([])

  // ── Form ─────────────────────────────────────────────────
  const formPedido = ref({
    numero_pedido:    "",
    fecha_recibo:     "",
    fecha_compromiso: "",
    cliente:          "",
    prioridad:        "media",
    notas:            "",
    venta_publico:     false,
    contacto_nombre:   "",
    contacto_estado:   "",
    contacto_prefijo:  "+52",
    contacto_telefono: "",
  })

  const CAMPO_LABELS = {
    numero_pedido:    "Número de pedido",
    cliente_id:       "Cliente",
    fecha_recibo:     "Fecha de recibo",
    fecha_compromiso: "Fecha compromiso",
    prioridad:        "Prioridad",
    notas:            "Notas",
    items:            "Artículos",
    non_field_errors: "Error general",
    venta_publico:    "Venta al público",
    contacto_nombre:  "Nombre del cliente",
    contacto_telefono:"Teléfono del cliente",
  }

  // ── Ítems ────────────────────────────────────────────────
  const items     = ref([])
  const itemNuevo = ref({ articulo_id: "", cantidad: null, notas: "" })

  // ── Dropdown artículo manual ─────────────────────────────
  const articuloBusqueda        = ref("")
  const mostrarDropdownArticulo = ref(false)

  // ── Computed ─────────────────────────────────────────────
  const puedeAvanzar = computed(() => {
    if (!formPedido.value.numero_pedido || !formPedido.value.fecha_recibo) return false
    if (formPedido.value.venta_publico) {
      return !!(formPedido.value.contacto_nombre && formPedido.value.contacto_telefono)
    }
    return !!formPedido.value.cliente
  })
  const totalPiezas        = computed(() => items.value.reduce((s, i) => s + (Number(i.cantidad) || 0), 0))
  const itemsConMatch      = computed(() => items.value.filter((i) =>  i.articulo_id).length)
  const itemsSinMatch      = computed(() => items.value.filter((i) => !i.articulo_id).length)
  const articulosFiltrados = computed(() => filtrarArticulos(articuloBusqueda.value))

  // ── Helpers catálogo ─────────────────────────────────────
  function codigoArticulo(id) {
    return catArticulos.value.find((a) => a.id === id)?.codigo || "—"
  }
  function descripcionArticulo(id) {
    const a = catArticulos.value.find((a) => a.id === id)
    return a?.descripcion || a?.codigo || "—"
  }
  function filtrarArticulos(texto) {
    const q = (texto || "").toLowerCase()
    return catArticulos.value
      .filter((a) => !q || a.codigo.toLowerCase().includes(q) || (a.descripcion || "").toLowerCase().includes(q))
      .slice(0, 5)
  }

  // ── Dropdown artículo ────────────────────────────────────
  function seleccionarArticulo(a) {
    itemNuevo.value.articulo_id = a.id
    articuloBusqueda.value = `${a.codigo} — ${a.descripcion || a.codigo}`
    mostrarDropdownArticulo.value = false
  }
  function cerrarDropdownArticulo() { mostrarDropdownArticulo.value = false }
  function asignarArticuloItem(item, a) { item.articulo_id = a.id; item._busqueda = ""; item._abierto = false }
  function cerrarDropdownItemIdx(idx)   { if (items.value[idx]) items.value[idx]._abierto = false }

  // ── PDF handlers ─────────────────────────────────────────
  function onPdfChange(e) { pdfFile.value = e.target.files[0] || null }
  function onDrop(e) {
    const f = e.dataTransfer.files[0]
    if (f?.type === "application/pdf") pdfFile.value = f
  }

  // ── Análisis PDF Microsip ─────────────────────────────────
  async function analizarPdf() {
    analizando.value = true
    try {
      const resultado = await analizarPdfPedido(pdfFile.value)
      if (resultado.folio && !formPedido.value.numero_pedido)
        formPedido.value.numero_pedido = parseInt(resultado.folio) || resultado.folio
      if (resultado.fecha && !formPedido.value.fecha_recibo)
        formPedido.value.fecha_recibo = resultado.fecha
      const rawItems = resultado.items.map((item) => ({
        articulo_id: item.articulo_id, cantidad: item.cantidad,
        notas: item.comentarios?.join(" | ") || "",
        _desdeIa: true, _busqueda: item.articulo_id ? codigoArticulo(item.articulo_id) : "", _abierto: false,
        nombre_pdf: item.nombre_pdf, codigo_pdf: item.codigo_pdf,
        comentarios: item.comentarios, confianza: item.confianza,
      }))
      items.value = _consolidarItems(rawItems)
      itemsDesdeIa.value = true
    } catch (err) {
      console.error("Error análisis PDF:", err)
      Swal.fire("Error", "No se pudo analizar el PDF. Puedes agregar artículos manualmente.", "warning")
      items.value = []; itemsDesdeIa.value = false
    } finally {
      analizando.value = false
    }
  }

  // ── Gestión ítems manuales ───────────────────────────────
  function agregarItem() {
    if (!itemNuevo.value.articulo_id || !itemNuevo.value.cantidad) return
    const existe = items.value.find((i) => i.articulo_id === itemNuevo.value.articulo_id)
    if (existe) {
      existe.cantidad += itemNuevo.value.cantidad
      if (itemNuevo.value.notas)
        existe.notas = existe.notas ? `${existe.notas} | ${itemNuevo.value.notas}` : itemNuevo.value.notas
    } else {
      items.value.push({
        articulo_id: itemNuevo.value.articulo_id, cantidad: itemNuevo.value.cantidad,
        notas: itemNuevo.value.notas || "", _desdeIa: false,
        _busqueda: codigoArticulo(itemNuevo.value.articulo_id), _abierto: false, confianza: 100,
      })
    }
    itemNuevo.value = { articulo_id: "", cantidad: null, notas: "" }
    articuloBusqueda.value = ""
  }
  function quitarItem(idx) { items.value.splice(idx, 1) }

  // ── Navegación wizard ────────────────────────────────────
  async function avanzar() {
    if (paso.value === 1) {
      if (!puedeAvanzar.value) return
      paso.value = 2
      if (pdfFile.value && items.value.length === 0) await analizarPdf()
      return
    }
    if (paso.value === 2) {
      const itemsValidos = items.value.filter(i => i.articulo_id)
      if (!itemsValidos.length) {
        Swal.fire("Atención", "Agrega al menos un artículo con coincidencia asignada.", "warning")
        return
      }
      paso.value = 3
    }
  }
  function retroceder() { if (paso.value > 1) paso.value-- }

  // ── Guardar pedido ───────────────────────────────────────
  function _capitalizarPrioridad(p) {
    return p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : "Media"
  }

  function _consolidarItems(lista) {
    const mapa = new Map()
    for (const item of lista) {
      if (!item.articulo_id) continue
      if (mapa.has(item.articulo_id)) {
        const base = mapa.get(item.articulo_id)
        base.cantidad += Number(item.cantidad) || 0
        if (item.notas) base.notas = base.notas ? `${base.notas} | ${item.notas}` : item.notas
      } else {
        mapa.set(item.articulo_id, { ...item, cantidad: Number(item.cantidad) || 0 })
      }
    }
    const sinAsignar = lista.filter((i) => !i.articulo_id)
    return [...mapa.values(), ...sinAsignar]
  }

  async function guardarPedido(onExito) {
    items.value = _consolidarItems(items.value)

    const itemsValidos = items.value.filter((i) => i.articulo_id)
    if (!itemsValidos.length) {
      Swal.fire("Atención", "Agrega al menos un artículo con coincidencia asignada.", "warning")
      return
    }

    guardando.value = true
    try {
      // Venta al público: se ignora el cliente elegido (no aplica) y se manda
      // el contacto real; el backend resuelve el cliente marcado como venta
      // al público y guarda el contacto en el propio pedido.
      const datosCliente = formPedido.value.venta_publico
        ? {
            venta_publico:     true,
            contacto_nombre:   formPedido.value.contacto_nombre.trim(),
            contacto_estado:   formPedido.value.contacto_estado || "",
            contacto_telefono: `${formPedido.value.contacto_prefijo} ${formPedido.value.contacto_telefono}`,
          }
        : { cliente_id: formPedido.value.cliente }

      // 1️⃣ Crear el pedido
      const pedidoCreado = await crearPedido({
        numero_pedido:    Number(formPedido.value.numero_pedido),
        ...datosCliente,
        fecha_recibo:     formPedido.value.fecha_recibo,
        fecha_compromiso: formPedido.value.fecha_compromiso || null,
        prioridad:        _capitalizarPrioridad(formPedido.value.prioridad),
        notas:            formPedido.value.notas || "",
        items: itemsValidos.map((i) => ({
          articulo_id: i.articulo_id,
          cantidad:    i.cantidad,
          notas:       i.notas || "",
        })),
      })

      // 2️⃣ Subir proyectos si hay — solo se envía el archivo
      if (proyectos.value.length > 0) {
        const numeroPedido = pedidoCreado?.numero_pedido ?? formPedido.value.numero_pedido
        const uploads = proyectos.value.map((p) => {
          const fd = new FormData()
          fd.append("archivo", p.archivo)
          return agregarProyectoPedido(numeroPedido, fd)
        })
        const resultados = await Promise.allSettled(uploads)
        const fallidos = resultados.filter(r => r.status === "rejected")
        if (fallidos.length > 0) {
          console.error("Proyectos con error al subir:", fallidos)
          Swal.fire({
            icon:  "warning",
            title: "Pedido guardado con advertencias",
            text:  `El pedido fue creado, pero ${fallidos.length} proyecto(s) no pudieron subirse. Puedes adjuntarlos desde la edición del pedido.`,
            confirmButtonColor: DIVISAN_COLORS.brown,
          })
          _limpiarObjectUrls()
          if (_stopWatchers) { _stopWatchers(); _stopWatchers = null }
          await limpiarTodoDraft()
          onExito?.()
          return
        }
      }

      if (_stopWatchers) { _stopWatchers(); _stopWatchers = null }
      await limpiarTodoDraft()
      _limpiarObjectUrls()

      Swal.fire({
        icon: "success", title: "Pedido creado",
        text: `Pedido #${formPedido.value.numero_pedido} guardado correctamente`,
        timer: 2500, showConfirmButton: false,
      })
      onExito?.()
    } catch (err) {
      console.error(err)
      const data = err.response?.data
      if (data && typeof data === "object") {
        const mensajes = Object.entries(data)
          .map(([campo, errores]) => {
            const label = CAMPO_LABELS[campo] || campo
            if (Array.isArray(errores) && typeof errores[0] === "object") {
              const detalle = errores.flatMap((obj) => Object.values(obj)).flat().join(", ")
              return `• ${label}: ${detalle}`
            }
            const lista = Array.isArray(errores) ? errores.join(", ") : errores
            return `• ${label}: ${lista}`
          })
          .join("\n")
        Swal.fire({ icon: "error", title: "No se pudo guardar el pedido", text: mensajes, confirmButtonColor: DIVISAN_COLORS.brown })
      } else {
        Swal.fire({ icon: "error", title: "Error de conexión", text: "No se pudo conectar con el servidor. Intenta de nuevo.", confirmButtonColor: DIVISAN_COLORS.brown })
      }
    } finally {
      guardando.value = false
    }
  }

  // ── Auto-guardado ─────────────────────────────────────────
  let _stopWatchers = null

  function stopAutoGuardado() {
    if (_stopWatchers) { _stopWatchers(); _stopWatchers = null }
  }

  function activarAutoGuardado() {
    if (_stopWatchers) return
    let debounce = null
    const stopForm = watch(
      [formPedido, items],
      () => {
        clearTimeout(debounce)
        debounce = setTimeout(() => guardarDraftLS(formPedido.value, items.value), 600)
      },
      { deep: true }
    )
    const stopPdf = watch(pdfFile, async (file) => {
      if (file) await guardarPdfIDB(file)
      else       await limpiarPdfIDB()
    })
    _stopWatchers = () => { stopForm(); stopPdf(); clearTimeout(debounce) }
  }

  // ── Restaurar borrador ────────────────────────────────────
  async function restaurarBorrador() {
    if (!hayDraftLS()) return false
    const draft = cargarDraftLS()
    if (!draft) return false

    const fechaStr = new Date(draft.ts).toLocaleString("es-MX", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    })
    const nItems = draft.items?.length ?? 0

    const { isConfirmed } = await Swal.fire({
      icon: "info",
      title: "Tienes un borrador guardado",
      html: `Pedido iniciado el <b>${fechaStr}</b> con <b>${nItems} artículo(s)</b>.<br><br>¿Quieres continuar donde lo dejaste?`,
      showCancelButton: true,
      confirmButtonColor: DIVISAN_COLORS.brown,
      cancelButtonColor: "#888",
      confirmButtonText: "Continuar borrador",
      cancelButtonText: "Descartar y empezar nuevo",
    })

    if (!isConfirmed) { await limpiarTodoDraft(); return false }

    formPedido.value = { ...formPedido.value, ...draft.formPedido }
    items.value = draft.items.map((item) => ({
      ...item,
      _busqueda: item.articulo_id ? codigoArticulo(item.articulo_id) : "",
      _abierto:  false,
    }))
    const pdf = await cargarPdfIDB()
    if (pdf) pdfFile.value = pdf
    if (items.value.some((i) => i._desdeIa)) itemsDesdeIa.value = true

    return true
  }

  // ── Helpers internos ─────────────────────────────────────
  function _limpiarObjectUrls() {
    proyectos.value.forEach(p => { if (p._objectUrl) URL.revokeObjectURL(p._objectUrl) })
  }

  // ── Reset completo ───────────────────────────────────────
  async function reset(limpiarPersistencia = false) {
    if (_stopWatchers) { _stopWatchers(); _stopWatchers = null }
    _limpiarObjectUrls()
    paso.value = 1; pdfFile.value = null; items.value = []; itemsDesdeIa.value = false
    proyectos.value = []
    articuloBusqueda.value = ""; mostrarDropdownArticulo.value = false
    itemNuevo.value = { articulo_id: "", cantidad: null, notas: "" }
    formPedido.value = {
      numero_pedido: "", fecha_recibo: "", fecha_compromiso: "",
      cliente: "", prioridad: "media", notas: "",
      venta_publico: false, contacto_nombre: "", contacto_estado: "",
      contacto_prefijo: "+52", contacto_telefono: "",
    }
    if (limpiarPersistencia) await limpiarTodoDraft()
  }

  const fechaLimite = computed(() => {
    if (!formPedido.value.fecha_recibo) return ""
    const d = new Date(formPedido.value.fecha_recibo + "T00:00:00")
    d.setDate(d.getDate() + 30)
    return d.toISOString().split("T")[0]
  })

  return {
    paso, pasos, guardando, analizando,
    pdfFile, itemsDesdeIa,
    formPedido, items, itemNuevo,
    proyectos,
    articuloBusqueda, mostrarDropdownArticulo,
    puedeAvanzar, totalPiezas, itemsConMatch, itemsSinMatch, articulosFiltrados,
    fechaLimite,
    codigoArticulo, descripcionArticulo, filtrarArticulos,
    seleccionarArticulo, cerrarDropdownArticulo,
    asignarArticuloItem, cerrarDropdownItemIdx,
    onPdfChange, onDrop,
    agregarItem, quitarItem,
    avanzar, retroceder,
    guardarPedido, reset,
    activarAutoGuardado, stopAutoGuardado, restaurarBorrador,
  }
}